import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';
import { DateTime, Duration } from 'luxon';
import React from 'react';
import styled from 'styled-components';

type Props = {
    daysWithHours: {
        [dayName: string]: string[];
    };
    showObs?: boolean;
    cellWidth: number;
    mode: 'cozy' | 'compact';
    whiteSpaceWidth: number;
};

export const generateDaysWithHours = (timeRange: string, timezone?: string) => {
    const hourRange = timeRange.split('--').map((hours) => Duration.fromISO(hours).as('hours')) as [number, number];

    const start = DateTime.utc()
        .setZone(timezone ?? 'Pacific/Auckland')
        .startOf('hour')
        .plus({ hours: hourRange[0] });

    //  create an array with a length equal to the difference between the start and end of hourRange.
    const groupedHrs = Array.from({ length: hourRange[1] - hourRange[0] }).reduce<{ [dayName: string]: string[] }>((groupedHrs, _, index) => {
        const theHour = index + start.hour; //the current hour based on the start hour and the current index.
        const dayIndex = Math.floor(theHour / 24); // the index of the day in the week (0-6) based on the current hour.
        const theDay = start.plus({ days: dayIndex }).startOf('day').toISO(); // the day associated with the current hour, formatted in ISO format.
        const hour = Math.floor(theHour % 24); // the hour of the day (0-23) based on the current hour.
        return { ...groupedHrs, [theDay]: [...(groupedHrs[theDay] ?? []), hour.toString()] };
    }, {});

    return groupedHrs;
};

export const DayRow = ({ daysWithHours, showObs = false, cellWidth, mode, whiteSpaceWidth }: Props) => {
    return (
        <TimeRow className='time-row' style={{ maxHeight: mode === 'cozy' ? 42 : 28 }} mode={mode}>
            <WhiteSpace secondary width={whiteSpaceWidth} />
            {showObs && (
                <DayDiv width={1} cellWidth={44}>
                    <DateDiv isDays></DateDiv>
                </DayDiv>
            )}
            {Object.entries(daysWithHours).map(([dayName, hours]) => {
                return (
                    <DayDiv key={dayName} width={hours.length} cellWidth={cellWidth}>
                        <DateDiv isDays>{DateTime.fromISO(dayName).toFormat('EEE dd MMM')}</DateDiv>
                    </DayDiv>
                );
            })}
        </TimeRow>
    );
};

export const HourRow = ({ daysWithHours, showObs = false, cellWidth, mode, whiteSpaceWidth }: Props) => {
    return (
        <TimeRow className='time-row' style={{ maxHeight: 18 }} mode={mode}>
            <WhiteSpace width={whiteSpaceWidth} />
            {showObs && (
                <DayDiv width={1} cellWidth={44}>
                    <DateDiv>Obs</DateDiv>
                </DayDiv>
            )}
            {Object.entries(daysWithHours)
                .map(([dayName, hours]) => {
                    return hours.map((hour) => {
                        return (
                            <DayDiv key={[dayName, hour].join()} width={1} cellWidth={cellWidth}>
                                <DateDiv>{hour}</DateDiv>
                            </DayDiv>
                        );
                    });
                })
                .flat()}
        </TimeRow>
    );
};

const TimeRow = styled.div<{ mode?: 'cozy' | 'compact' }>`
    position: relative;
    display: flex;
    flex-grow: 1;
    min-width: 100%;
    color: ${(props) => props.theme.text};
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4};
`;

const DayDiv = styled.div<{ width: number; cellWidth: number }>`
    min-width: ${(props) => props.width * props.cellWidth}px;
`;

const DateDiv = styled.div<{ isDays?: boolean }>`
    display: inline-flex;
    align-items: center;
    min-width: 100%;
    height: 100%;
    background: ${(props) => (props.isDays ? props.theme.secondary : 'none')};
    white-space: nowrap;
    overflow: hidden;
`;

const WhiteSpace = styled.div<{ secondary?: boolean; width: number }>`
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: ${(props) => props.width}px;

    background-color: ${(props) => (props.secondary ? props.theme.secondary : props.theme.background)};
`;
